import ContactEditField from 'flow/components/contact-components/ContactEditField';
import NumberField from 'flow/components/custom-components/NumberFieldSmart';
import FlowInfoTextBetween from 'flow/components/text-components/FlowInfotextBetween';
import Link from 'flow/components/ui-components/Link';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import {
    ADDRESS_KEY,
    AddressFields,
    ContactFieldOrder,
    ContactFieldType,
    NameFields,
    shouldRenderContactField,
} from 'flow/utils/contact-utils';
import { Capabilities } from 'flow/utils/capability-utils';

const ContactEditFields = ({
    contactFields,
    updateContactString,
    updateContactNumber,
    userCountryCode,
    showEmailEditHint,
    navigateAuthenticationSettings,
    disableEdit = false,
    extensionKey,
}) => {
    const { i18next } = useAppContext();

    const shouldDisplayMobileField = useAppSelector((state) =>
        extensionKey
            ? selectHasCapability(
                  state,
                  extensionKey,
                  Capabilities.MOBILEEXTENSION,
              )
            : true,
    );

    const renderRemainingFields = () => {
        return contactFields
            .filter(shouldRenderContactField)
            .map(renderEditableField)
            .valueSeq();
    };

    const renderOrderedFields = (fieldKey) => {
        const fieldData = contactFields.get(fieldKey, Map());
        if (fieldKey === ADDRESS_KEY) {
            return contactFields
                .filter((_fieldValue, fieldKey) =>
                    AddressFields.includes(fieldKey),
                )
                .map(renderEditableField)
                .valueSeq();
        }
        return renderEditableField(fieldData, fieldKey);
    };

    const renderEditableField = (fieldData, fieldKey) => {
        if (typeof fieldData === 'string') {
            return (
                <ContactEditField
                    key={fieldKey}
                    field_key={fieldKey}
                    field_value={fieldData}
                    field_func={updateContactString}
                    label={fieldData.get('label')}
                    disableEdit={disableEdit}
                />
            );
        }

        const fieldLabel = fieldData.get('label');
        const fieldType = fieldData.get('fieldType');

        if (ContactFieldType.BASIC_STRING.is(fieldType)) {
            return (
                <ContactEditField
                    key={`editable-${fieldLabel}`}
                    field_key={fieldKey}
                    field_value={fieldData}
                    field_func={updateContactString}
                    label={fieldData.get('label')}
                    disableEdit={disableEdit}
                />
            );
        } else if (ContactFieldType.NUMBER.is(fieldType)) {
            const fieldNumberValue = fieldData.get('number');
            const isEditable = fieldData.get('editable');

            if (fieldKey === 'mobile' && !shouldDisplayMobileField) {
                return null;
            }

            return (
                <NumberField
                    key={`editable-${fieldLabel}`}
                    id={fieldKey}
                    numberDTO={fieldNumberValue}
                    onChange={updateContactNumber}
                    label={fieldLabel}
                    disabled={disableEdit || !isEditable}
                    defaultCountryCode={userCountryCode}
                />
            );
        } else if (ContactFieldType.EMAIL.is(fieldType)) {
            return (
                <div key={`editable-${fieldLabel}`}>
                    <div className="flex flex-col gap-2">
                        <ContactEditField
                            field_key={fieldKey}
                            field_value={fieldData}
                            field_func={updateContactString}
                            label={fieldData.get('label')}
                        />
                        {renderEmailHint()}
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderEmailHint = () => {
        if (!showEmailEditHint) {
            return null;
        }

        const description = (
            <p className="text-xs text-gray-400">
                {i18next.t('ContactCard.Items.Email.Hint')}
                <Link onClick={navigateAuthenticationSettings} className="ms-1">
                    {i18next.t('settings.user.credential.username_password')}
                </Link>
            </p>
        );

        return <FlowInfoTextBetween>{description}</FlowInfoTextBetween>;
    };

    return (
        <div className="flex flex-col gap-6">
            {NameFields.map(renderOrderedFields)}
            {ContactFieldOrder.map(renderOrderedFields)}
            {renderRemainingFields()}
        </div>
    );
};

ContactEditFields.propTypes = {
    contactFields: PropTypes.instanceOf(Map),
    updateContactString: PropTypes.func.isRequired,
    updateContactNumber: PropTypes.func.isRequired,
    userCountryCode: PropTypes.string,
    showEmailEditHint: PropTypes.bool.isRequired,
    navigateAuthenticationSettings: PropTypes.func,
    disableEdit: PropTypes.bool,
    extensionKey: PropTypes.string,
};

ContactEditFields.defaultProps = {
    showEmailEditHint: false,
};

export default ContactEditFields;
